import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import { useTranslation } from 'react-i18next';
import {
  DELIVERY_DASHBOARD_PAGE_TRACKING_EVENTS,
  TDashboardCourierDetailViewData,
  TDashboardCourierListViewData,
  TDashboardMapInteractionData,
  TDashboardOrderDetailViewData,
  TDashboardOrderListFilteredViewData,
  TDashboardOrderListViewData,
} from '.';
import { trackByEvent } from '@src/tracking/services';
import { COMPONENT_TYPE } from '@src/tracking/constant';
import { useEffect, useRef, useState } from 'react';

const useDeliveryDashboadAnalytics = () => {
  const { i18n } = useTranslation();
  const { restaurant } = useKeycloakUserState();
  const [country, setCountry] = useState('');
  const [language, setLanguage] = useState(i18n.language);
  const CALLED_TRACKING_EVENTS = useRef({});

  const eventCategory = '';

  useEffect(() => {
    setLanguage(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    setCountry(restaurant?.country || '');
  }, [restaurant]);

  const registerEventAsCalled = (registryName: string) => {
    return {
      once: () => {
        CALLED_TRACKING_EVENTS.current = {
          ...CALLED_TRACKING_EVENTS.current,
          [registryName]: true,
        };
      },
    };
  };
  const isEventCalledAlready = (registryName: string) => {
    const registry = CALLED_TRACKING_EVENTS.current as any;
    return registry[registryName];
  };

  const trackCourierListView = (courierListCount: number) => {
    const {
      event,
      component_name,
      component_type,
      list_action,
      $registryName,
    } = DELIVERY_DASHBOARD_PAGE_TRACKING_EVENTS.COURIER_LIST_VIEW;

    if (isEventCalledAlready($registryName)) {
      return;
    }

    try {
      const trackData: TDashboardCourierListViewData = {
        component_name,
        component_type: component_type as COMPONENT_TYPE,
        list_action,
        list_countsTotal: courierListCount.toString(),
        country,
        language,
        eventCategory,
      };
      trackByEvent<TDashboardCourierListViewData>(event, trackData);
      return registerEventAsCalled($registryName);
    } catch (error) {
      console.log('trackCourierListView error', error);
    }
  };

  const trackOrderListView = (filterType: string, orderListCount: number) => {
    const { event, component_name, component_type, $registryName } =
      DELIVERY_DASHBOARD_PAGE_TRACKING_EVENTS.ORDER_LIST_VIEW;

    if (isEventCalledAlready($registryName)) {
      return;
    }
    try {
      const trackData: TDashboardCourierListViewData = {
        component_name,
        component_type: component_type as COMPONENT_TYPE,
        list_action: `view: filter - ${filterType}`,
        list_countsTotal: orderListCount.toString(),
        country,
        language,
        eventCategory,
      };
      trackByEvent<TDashboardOrderListViewData>(event, trackData);
      return registerEventAsCalled($registryName);
    } catch (error) {
      console.log('trackCourierListView error', error);
    }
  };

  const trackFilteredOrderListView = (
    filterType: string,
    orderListCount: number
  ) => {
    const { event, component_name, component_type, $registryName } =
      DELIVERY_DASHBOARD_PAGE_TRACKING_EVENTS.FILTERED_ORDER_LIST_VIEW;

    if (isEventCalledAlready($registryName)) {
      return;
    }

    try {
      const trackData: TDashboardOrderListFilteredViewData = {
        component_name,
        component_type: component_type as COMPONENT_TYPE,
        list_action: `filter - ${filterType}`,
        list_countsTotal: orderListCount.toString(),
        country,
        language,
        eventCategory,
      };
      trackByEvent<TDashboardOrderListFilteredViewData>(event, trackData);
      return registerEventAsCalled($registryName);
    } catch (error) {
      console.log('trackFilteredOrderListView error', error);
    }
  };

  const trackOrderDetailView = (orderHash: string, orderId: string) => {
    const {
      event,
      component_name,
      component_type,
      component_source,
      order_action,
      $registryName,
    } = DELIVERY_DASHBOARD_PAGE_TRACKING_EVENTS.ORDER_DETAIL_VIEW;
    if (isEventCalledAlready($registryName)) {
      return;
    }
    try {
      const trackData: TDashboardOrderDetailViewData = {
        component_name,
        component_type: component_type as COMPONENT_TYPE,
        component_source,
        order_hashed: orderHash,
        order_id: orderId,
        order_action,
        country,
        language,
        eventCategory,
      };
      trackByEvent<TDashboardOrderDetailViewData>(event, trackData);
      return registerEventAsCalled($registryName);
    } catch (error) {
      console.log('trackOrderDetailView error', error);
    }
  };

  const trackOrderDetailViewViaMap = (orderHash: string, orderId: string) => {
    const {
      event,
      component_name,
      component_type,
      component_source,
      order_action,
      $registryName,
    } = DELIVERY_DASHBOARD_PAGE_TRACKING_EVENTS.ORDER_DETAIL_VIEW_VIA_MAP;
    if (isEventCalledAlready($registryName)) {
      return;
    }
    try {
      const trackData: TDashboardOrderDetailViewData = {
        component_name,
        component_type: component_type as COMPONENT_TYPE,
        component_source,
        order_hashed: orderHash,
        order_id: orderId,
        order_action,
        country,
        language,
        eventCategory,
      };
      trackByEvent<TDashboardOrderDetailViewData>(event, trackData);
      return registerEventAsCalled($registryName);
    } catch (error) {
      console.log('trackOrderDetailView error', error);
    }
  };

  const trackCourierDetailView = (courierId: string) => {
    const {
      event,
      component_name,
      component_type,
      component_source,
      $registryName,
    } = DELIVERY_DASHBOARD_PAGE_TRACKING_EVENTS.COURIER_DETAIL_VIEW;
    if (isEventCalledAlready($registryName)) {
      return;
    }
    try {
      const trackData: TDashboardCourierDetailViewData = {
        component_name,
        component_type: component_type as COMPONENT_TYPE,
        component_source,
        courierDetail: window.btoa(courierId),
        country,
        language,
        eventCategory,
      };
      trackByEvent<TDashboardCourierDetailViewData>(event, trackData);
      return registerEventAsCalled($registryName);
    } catch (error) {
      console.log('trackCourierDetailView error', error);
    }
  };

  const trackCourierDetailViewViaMap = (courierId: string) => {
    const {
      event,
      component_name,
      component_type,
      component_source,
      $registryName,
    } = DELIVERY_DASHBOARD_PAGE_TRACKING_EVENTS.COURIER_DETAIL_VIEW_VIA_MAP;

    if (isEventCalledAlready($registryName)) {
      return;
    }
    try {
      const trackData: TDashboardCourierDetailViewData = {
        component_name,
        component_type: component_type as COMPONENT_TYPE,
        component_source,
        courierDetail: window.btoa(courierId),
        country,
        language,
        eventCategory,
      };
      trackByEvent<TDashboardCourierDetailViewData>(event, trackData);
      return registerEventAsCalled($registryName);
    } catch (error) {
      console.log('trackCourierDetailViewViaMap error', error);
    }
  };

  const trackMapInteraction = () => {
    const { event, component_name, component_type, map_action, $registryName } =
      DELIVERY_DASHBOARD_PAGE_TRACKING_EVENTS.MAP_INTERACTION;
    if (isEventCalledAlready($registryName)) {
      return;
    }
    try {
      const trackData: TDashboardMapInteractionData = {
        component_name,
        component_type: component_type as COMPONENT_TYPE,
        map_action,
        country,
        language,
        eventCategory,
      };
      trackByEvent<TDashboardMapInteractionData>(event, trackData);
      return registerEventAsCalled($registryName);
    } catch (error) {
      console.log('trackCourierDetailViewViaMap error', error);
    }
  };

  return {
    trackCourierListView,
    trackOrderListView,
    trackFilteredOrderListView,
    trackCourierDetailView,
    trackCourierDetailViewViaMap,
    trackOrderDetailView,
    trackOrderDetailViewViaMap,
    trackMapInteraction,
  };
};

export default useDeliveryDashboadAnalytics;
